import { useState } from 'react';
import { apiFetch } from '../hooks/useApi';
import type { ChildWriteResponse } from '@shared/api-responses';

export interface ChildField {
  name: string;
  label: string;
  /** Hint shown as input placeholder (e.g. date format). */
  placeholder?: string;
}

/** Any record carrying a numeric id — the typed section arrays
 *  (EmergencyContact[], Physical[], …) all satisfy this structurally. */
export interface ChildRow {
  id: number;
}

function get(row: ChildRow, key: string): unknown {
  return (row as unknown as Record<string, unknown>)[key];
}

interface Props {
  title: string;
  /** API slug, e.g. "emergency-contacts". */
  slug: string;
  coId: number;
  items: ChildRow[];
  /** Editable fields, in display/form order. The first is rendered as the
   *  row's primary (bold) line; the rest as a secondary detail line. */
  fields: ChildField[];
  canEdit: boolean;
  /** Called after a successful create/update/delete so the parent can refetch. */
  onChanged: () => void;
}

const inputClass =
  'w-full rounded-lg border border-border-default bg-surface-raised px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-dcvfd-accent';

function emptyDraft(fields: ChildField[]): Record<string, string> {
  return Object.fromEntries(fields.map((f) => [f.name, '']));
}

function rowToDraft(fields: ChildField[], row: ChildRow): Record<string, string> {
  return Object.fromEntries(fields.map((f) => [f.name, get(row, f.name) == null ? '' : String(get(row, f.name))]));
}

export default function ChildCollection({ title, slug, coId, items, fields, canEdit, onChanged }: Props) {
  const [mode, setMode] = useState<'view' | 'add' | number>('view'); // number = editing that row id
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startAdd() {
    setDraft(emptyDraft(fields));
    setError(null);
    setMode('add');
  }

  function startEdit(row: ChildRow) {
    setDraft(rowToDraft(fields, row));
    setError(null);
    setMode(row.id);
  }

  function cancel() {
    setMode('view');
    setError(null);
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      if (mode === 'add') {
        await apiFetch<ChildWriteResponse>(`/members/${coId}/${slug}`, { method: 'POST', body: draft });
      } else if (typeof mode === 'number') {
        await apiFetch<ChildWriteResponse>(`/${slug}/${mode}`, { method: 'PUT', body: draft });
      }
      setMode('view');
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  async function remove(row: ChildRow) {
    if (!window.confirm(`Delete this ${title.replace(/s$/, '').toLowerCase()}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/${slug}/${row.id}`, { method: 'DELETE' });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    } finally {
      setBusy(false);
    }
  }

  function renderForm() {
    return (
      <div className="rounded-lg border border-dcvfd-accent/40 bg-surface p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          {fields.map((f) => (
            <div key={f.name}>
              <label
                className="text-[11px] uppercase tracking-wide text-zinc-400 mb-0.5 block"
                htmlFor={`${slug}-${f.name}`}
              >
                {f.label}
              </label>
              <input
                id={`${slug}-${f.name}`}
                value={draft[f.name] ?? ''}
                placeholder={f.placeholder}
                onChange={(e) => setDraft((d) => ({ ...d, [f.name]: e.target.value }))}
                className={inputClass}
              />
            </div>
          ))}
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2">
          <button
            onClick={submit}
            disabled={busy}
            className="rounded-md bg-dcvfd-accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={cancel}
            disabled={busy}
            className="rounded-md border border-border-default px-3 py-1.5 text-xs hover:bg-surface-raised"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  const [primary, ...rest] = fields;

  return (
    <section className="rounded-lg border border-border-subtle bg-surface-raised">
      <h2 className="flex items-center justify-between border-b border-border-subtle px-4 py-2.5 text-sm font-semibold text-dcvfd-dark">
        <span>
          {title} <span className="text-zinc-400 font-normal">({items.length})</span>
        </span>
        {canEdit && mode === 'view' && (
          <button onClick={startAdd} className="text-xs font-medium text-dcvfd-accent hover:underline">
            + Add
          </button>
        )}
      </h2>
      <div className="p-4 space-y-2">
        {items.length === 0 && mode !== 'add' && <p className="text-sm text-zinc-400">None on file.</p>}

        {items.map((row) =>
          mode === row.id ? (
            <div key={row.id}>{renderForm()}</div>
          ) : (
            <div key={row.id} className="flex items-start justify-between gap-2 text-sm">
              <div>
                <span className="font-medium">{String(get(row, primary.name) ?? '—')}</span>
                {rest.length > 0 && (
                  <span className="block text-zinc-500">
                    {rest
                      .map((f) => get(row, f.name))
                      .filter((v) => v != null && v !== '')
                      .join(' · ') || ' '}
                  </span>
                )}
              </div>
              {canEdit && mode === 'view' && (
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => startEdit(row)} className="text-xs text-dcvfd-accent hover:underline">
                    Edit
                  </button>
                  <button onClick={() => remove(row)} className="text-xs text-red-600 hover:underline">
                    Delete
                  </button>
                </div>
              )}
            </div>
          ),
        )}

        {mode === 'add' && renderForm()}
      </div>
    </section>
  );
}
